import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, imgUrl } from "../lib/api";
import { WorkerLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Download, ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight, Images } from "lucide-react";

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewingIdx, setViewingIdx] = useState(0);
  const [downloading, setDownloading] = useState({});
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => { loadProduct(); }, [productId]);

  const loadProduct = async () => {
    try {
      const r = await api.get(`/products/${productId}`);
      setProduct(r.data);
    } catch (e) {
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  // Build deduplicated list of all images for this product
  const allImages = useMemo(() => {
    if (!product) return [];
    const seen = new Set();
    const ids = [
      product.base_image_id,
      ...(product.additional_image_ids || []),
      ...(product.variation_image_ids || []),
      product.refined_image_id,
      product.image_asset_id,
    ].filter(id => {
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return ids;
  }, [product]);

  const safeName = (product?.name || "product").replace(/[^a-z0-9]/gi, "_");

  const handleDownload = async (imageId, label) => {
    setDownloading(d => ({ ...d, [imageId]: true }));
    try {
      const r = await api.get(`/images/${imageId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName}_${label || "image"}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed — please try again");
    } finally {
      setDownloading(d => ({ ...d, [imageId]: false }));
    }
  };

  const handleDownloadAll = async () => {
    if (!allImages.length) return;
    setDownloadingAll(true);
    for (let i = 0; i < allImages.length; i++) {
      await handleDownload(allImages[i], `view_${i + 1}`);
      if (i < allImages.length - 1) await new Promise(r => setTimeout(r, 400));
    }
    setDownloadingAll(false);
    toast.success(`All ${allImages.length} images downloaded!`);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/products/${productId}/complete`);
      toast.success("Product record saved! ✓");
      loadProduct();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <WorkerLayout>
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-[#A1B4A8]">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        Loading product…
      </div>
    </WorkerLayout>
  );

  if (!product) return (
    <WorkerLayout>
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-[#A1B4A8]">
        <Images className="w-12 h-12 opacity-30" />
        Product not found.
        <Button onClick={() => navigate("/worker")} variant="outline" className="border-[#21362A] text-[#A1B4A8] mt-2">
          Back to Dashboard
        </Button>
      </div>
    </WorkerLayout>
  );

  const isCompleted = product.image_workflow_status === "completed" || product.status === "completed";
  const currentImg = allImages[viewingIdx];
  const hasMultiple = allImages.length > 1;

  return (
    <WorkerLayout>
      <div className="max-w-5xl mx-auto space-y-5">

        {/* ── Header ── */}
        <div className="flex items-start gap-3">
          <Button onClick={() => navigate("/worker")} variant="ghost" size="icon" className="text-[#A1B4A8] mt-1 flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="label-overline text-[#D4AF37] text-xs">Product Details</div>
            <h1 className="font-display text-2xl sm:text-3xl mt-0.5 leading-tight">{product.name}</h1>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="label-overline text-xs text-[#A1B4A8]">Price</div>
            <div className="font-display text-2xl text-[#D4AF37]">${product.final_price}</div>
          </div>
        </div>

        {/* ── Status badges ── */}
        <div className="flex gap-2 flex-wrap">
          <Badge className={isCompleted ? "bg-green-500/20 text-green-300 border border-green-500/30" : "bg-blue-500/20 text-blue-300 border border-blue-500/30"}>
            {isCompleted ? "✓ Saved" : "Draft — needs saving"}
          </Badge>
          {product.category && <Badge className="bg-[#21362A] text-[#A1B4A8]">{product.category}</Badge>}
          <Badge className="bg-[#132018] text-[#A1B4A8] border border-[#21362A]">
            <Images className="w-3 h-3 mr-1" />
            {allImages.length} image{allImages.length !== 1 ? "s" : ""}
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* ── LEFT: Image gallery ── */}
          <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="font-display text-xl">Product Images</CardTitle>
              {allImages.length > 1 && (
                <Button
                  onClick={handleDownloadAll}
                  disabled={downloadingAll}
                  size="sm"
                  variant="outline"
                  className="border-[#21362A] text-[#A1B4A8] hover:text-white text-xs gap-1"
                >
                  <Download className="w-3 h-3" />
                  {downloadingAll ? "Downloading…" : `Download All (${allImages.length})`}
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {allImages.length === 0 ? (
                <div className="aspect-square bg-[#132018] rounded-sm border border-[#21362A] flex flex-col items-center justify-center text-[#A1B4A8] gap-3">
                  <Images className="w-12 h-12 opacity-20" />
                  <p className="text-sm text-center px-4">No images attached to this product yet.</p>
                </div>
              ) : (
                <>
                  {/* ── Main viewer ── */}
                  <div className="relative aspect-square bg-[#132018] rounded-sm overflow-hidden border border-[#21362A]">
                    <img
                      src={imgUrl(currentImg)}
                      alt={`Product image ${viewingIdx + 1} of ${allImages.length}`}
                      className="w-full h-full object-cover"
                    />

                    {/* Prev / Next arrows */}
                    {hasMultiple && (
                      <>
                        <button
                          onClick={() => setViewingIdx(i => Math.max(0, i - 1))}
                          disabled={viewingIdx === 0}
                          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/70 flex items-center justify-center disabled:opacity-20 hover:bg-black/90 transition-opacity"
                          aria-label="Previous image"
                        >
                          <ChevronLeft className="w-5 h-5 text-white" />
                        </button>
                        <button
                          onClick={() => setViewingIdx(i => Math.min(allImages.length - 1, i + 1))}
                          disabled={viewingIdx === allImages.length - 1}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/70 flex items-center justify-center disabled:opacity-20 hover:bg-black/90 transition-opacity"
                          aria-label="Next image"
                        >
                          <ChevronRight className="w-5 h-5 text-white" />
                        </button>
                      </>
                    )}

                    {/* Bottom bar */}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent pt-8 pb-3 px-3 flex items-center justify-between">
                      <span className="text-white text-xs font-medium">
                        {viewingIdx === 0 ? "🖼 Base image" : `📷 View ${viewingIdx + 1}`}
                        {hasMultiple && <span className="text-white/50 ml-2">{viewingIdx + 1} / {allImages.length}</span>}
                      </span>
                      <Button
                        onClick={() => handleDownload(currentImg, `view_${viewingIdx + 1}`)}
                        disabled={downloading[currentImg]}
                        size="sm"
                        className="bg-white/20 hover:bg-white/30 text-white border-0 h-7 text-xs gap-1"
                      >
                        <Download className="w-3 h-3" />
                        {downloading[currentImg] ? "Saving…" : "Download"}
                      </Button>
                    </div>
                  </div>

                  {/* ── Thumbnails ── */}
                  {hasMultiple && (
                    <div className="flex gap-1.5 overflow-x-auto pb-0.5">
                      {allImages.map((id, i) => (
                        <button
                          key={id}
                          onClick={() => setViewingIdx(i)}
                          title={i === 0 ? "Base image" : `View ${i + 1}`}
                          className={`flex-shrink-0 w-14 h-14 rounded border-2 overflow-hidden transition-all ${
                            i === viewingIdx
                              ? "border-[#D4AF37] ring-1 ring-[#D4AF37]/50"
                              : "border-[#21362A] opacity-60 hover:opacity-100 hover:border-[#A1B4A8]"
                          }`}
                        >
                          <img src={imgUrl(id)} alt={`thumb ${i + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* ── Per-image download buttons ── */}
                  <div className="grid grid-cols-2 gap-1.5 pt-1">
                    {allImages.map((id, i) => (
                      <Button
                        key={id}
                        onClick={() => handleDownload(id, `view_${i + 1}`)}
                        disabled={downloading[id]}
                        variant="outline"
                        size="sm"
                        className="border-[#21362A] text-[#A1B4A8] hover:text-white hover:border-[#A1B4A8] text-xs justify-start gap-1.5"
                      >
                        <Download className="w-3 h-3 flex-shrink-0" />
                        {downloading[id] ? "Saving…" : i === 0 ? "Base image" : `View ${i + 1}`}
                      </Button>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* ── RIGHT: Product info + Save ── */}
          <div className="space-y-4">
            {/* Save button — prominent, always visible */}
            <Button
              onClick={handleSave}
              disabled={saving || isCompleted}
              className={`w-full h-14 text-base font-semibold rounded-sm gap-2 ${
                isCompleted
                  ? "bg-green-800/30 text-green-400 border border-green-700/40 cursor-default"
                  : "bg-[#097969] hover:bg-[#0a8a78] text-white"
              }`}
            >
              <CheckCircle2 className="w-5 h-5" />
              {saving ? "Saving…" : isCompleted ? "Product Already Saved ✓" : "Save Product Record"}
            </Button>
            {!isCompleted && (
              <p className="text-xs text-center text-[#A1B4A8] -mt-2">
                Download the images first, then click Save to complete this product.
              </p>
            )}

            {/* Product details */}
            <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-display text-xl">Product Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="label-overline text-xs mb-1">Short Description</div>
                  <p className="text-sm text-[#A1B4A8] leading-relaxed">{product.short_description}</p>
                </div>
                <div>
                  <div className="label-overline text-xs mb-1">Full Description</div>
                  <p className="text-xs text-[#A1B4A8] leading-relaxed whitespace-pre-line">{product.full_description}</p>
                </div>
                {product.tags?.length > 0 && (
                  <div>
                    <div className="label-overline text-xs mb-1">Tags</div>
                    <div className="flex gap-1 flex-wrap">
                      {product.tags.map(t => (
                        <Badge key={t} className="bg-[#132018] text-[#A1B4A8] border border-[#21362A] text-[11px]">{t}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs pt-2 border-t border-[#21362A]">
                  {[
                    ["SKU",      product.sku],
                    ["Sizes",    product.sizes?.join(", ")],
                    ["Brand",    product.brand],
                    ["Category", product.category],
                  ].map(([label, val]) => val && (
                    <div key={label}>
                      <div className="text-[#667f6a] mb-0.5">{label}</div>
                      <div className="text-[#A1B4A8] truncate">{val}</div>
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t border-[#21362A] text-xs text-[#667f6a]">
                  Created by {product.generated_by_name} · {product.generated_at ? new Date(product.generated_at).toLocaleString() : "—"}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </WorkerLayout>
  );
}
